import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import i18n from './i18n';
import { Toaster } from './components/ui/sonner';

// Layout
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import MobileTicketsBar from './components/layout/MobileTicketsBar';
import CookieBanner from './components/layout/CookieBanner';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import PageTransition from './components/PageTransition';
import FloatingSpotifyPlayer from './components/FloatingSpotifyPlayer';
import Analytics from './components/Analytics';

// Pages
import HomePage from './pages/HomePage';
import AgendaPage from './pages/AgendaPage';
import HistoirePage from './pages/HistoirePage';
import VideosPage from './pages/VideosPage';
import IdeologiePage from './pages/IdeologiePage';
import ExperiencePage from './pages/ExperiencePage';
import BookingPage from './pages/BookingPage';
import AppPage from './pages/AppPage';
import GalleryPage from './pages/GalleryPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import SupportPage from './pages/SupportPage';
import EspanolPage from './pages/EspanolPage';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminHeroPage from './pages/admin/AdminHeroPage';
import AdminEventsPage from './pages/admin/AdminEventsPage';
import AdminTicketsPage from './pages/admin/AdminTicketsPage';
import AdminVideosPage from './pages/admin/AdminVideosPage';
import AdminTimelinePage from './pages/admin/AdminTimelinePage';
import AdminBookingsPage from './pages/admin/AdminBookingsPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminValuesPage from './pages/admin/AdminValuesPage';
import AdminFAQPage from './pages/admin/AdminFAQPage';
import AdminContentPage from './pages/admin/AdminContentPage';
import AdminGalleryPage from './pages/admin/AdminGalleryPage';
import AdminTickerPage from './pages/admin/AdminTickerPage';
import AdminHistoirePage from './pages/admin/AdminHistoirePage';
import AdminExperiencePage from './pages/admin/AdminExperiencePage';

import './App.css';

/**
 * Public routes wrapped in PageTransition. Lives in its own component
 * so useLocation() has access to the router context and AnimatePresence
 * re-keys on each route change.
 *
 * Admin routes intentionally bypass this — they use AdminLayout's own
 * transitions.
 */
const PublicRoutes = () => {
  const location = useLocation();

  return (
    <PageTransition>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<HomePage />} />
        <Route path="/agenda" element={<AgendaPage />} />
        <Route path="/histoire" element={<HistoirePage />} />
        <Route path="/videos" element={<VideosPage />} />
        <Route path="/galerie" element={<GalleryPage />} />
        <Route path="/ideologie" element={<IdeologiePage />} />
        <Route path="/experience" element={<ExperiencePage />} />
        <Route path="/booking" element={<BookingPage />} />
        <Route path="/app" element={<AppPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/es" element={<EspanolPage />} />
      </Routes>
    </PageTransition>
  );
};

function App() {
  return (
    <HelmetProvider>
      <I18nextProvider i18n={i18n}>
        <ErrorBoundary>
        <Analytics />
        <Router>
          <ScrollToTop />
          <Routes>
          {/* Admin Routes (no page transitions - AdminLayout handles its own) */}
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route path="/admin/*" element={<AdminLayout />}>
            <Route path="dashboard" element={<AdminDashboardPage />} />
            <Route path="hero" element={<AdminHeroPage />} />
            <Route path="events" element={<AdminEventsPage />} />
            <Route path="tickets" element={<AdminTicketsPage />} />
            <Route path="videos" element={<AdminVideosPage />} />
            <Route path="timeline" element={<AdminTimelinePage />} />
            <Route path="histoire" element={<AdminHistoirePage />} />
            <Route path="experience" element={<AdminExperiencePage />} />
            <Route path="bookings" element={<AdminBookingsPage />} />
            <Route path="values" element={<AdminValuesPage />} />
            <Route path="faqs" element={<AdminFAQPage />} />
            <Route path="content" element={<AdminContentPage />} />
            <Route path="gallery" element={<AdminGalleryPage />} />
            <Route path="ticker" element={<AdminTickerPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
          </Route>

          {/* Public Routes */}
          <Route path="/*" element={
            <div className="min-h-screen bg-[#060606] text-[#F5F0E8]">
              <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-4 focus:left-4 focus:px-4 focus:py-2 focus:bg-[#C9A84C] focus:text-[#060606] focus:rounded-lg focus:font-display focus:uppercase focus:tracking-wider">
                Aller au contenu principal
              </a>
              <Navbar />
              <main id="main-content">
                <PublicRoutes />
              </main>
              <Footer />
              <MobileTicketsBar />
              <CookieBanner />
              <FloatingSpotifyPlayer />
            </div>
          } />
        </Routes>
        <Toaster position="top-center" richColors />
      </Router>
        </ErrorBoundary>
    </I18nextProvider>
    </HelmetProvider>
  );
}

export default App;
